import React, { useState } from "react";
import authFetch from "../authFetch";

/* Same canonical glossary as TranslateInline so a category, tag, name,
   or description gets the same Arabic term whether it was translated
   inline by the viewer or up-front by the author. */
const GLOSSARY = {
  enToAr: {
    "expense":"مصاريف","income":"دخل","realised":"محقق","unrealised":"غير محقق",
    "recurring":"متكرر","one-off":"دفعة واحدة","investment":"استثمار",
    "approved":"معتمد","pending":"معلق","rejected":"مرفوض","cancelled":"ملغي",
    "vehicle":"مركبة","driver":"السائق","maintenance":"صيانة","insurance":"تأمين",
    "category":"فئة","subcategory":"فئة فرعية","supplier":"مورد",
    "project":"مشروع","salary":"راتب","loan":"قرض","invoice":"فاتورة",
  },
  arToEn: {
    "مصاريف":"expense","دخل":"income","محقق":"realised","غير محقق":"unrealised",
    "متكرر":"recurring","دفعة واحدة":"one-off","استثمار":"investment",
    "معتمد":"approved","معلق":"pending","مرفوض":"rejected","ملغي":"cancelled",
    "مركبة":"vehicle","السائق":"driver","صيانة":"maintenance","تأمين":"insurance",
    "فئة":"category","فئة فرعية":"subcategory","مورد":"supplier",
  },
};
function glossaryClause(target) {
  const map = target === "ar" ? GLOSSARY.enToAr : GLOSSARY.arToEn;
  return ` Use this glossary for consistency: ${Object.entries(map).map(([k,v])=>`${k}→${v}`).join("; ")}.`;
}

/* ══ AI TRANSLATE BUTTON ════════════════════════════════════
   Tiny button shown next to a translated counterpart input. When the
   primary text is filled and the counterpart is empty, click → call
   /api/ai-chat with Haiku to translate, then call onResult(translation).

   Props:
     source       — the text to translate (read at click time)
     target       — "ar" | "en" — translation direction
     domain       — short hint passed to the prompt to bias the model
                    ("category", "tag", "label", "name", "description").
                    Defaults to "label".
     onResult(s)  — called with the translation string
     disabled     — disable when source is empty or target is filled
     style, lang  — passthrough; lang controls the button tooltip text */
export default function AiTranslateBtn({ source, target = "ar", domain = "label", onResult, disabled, style, lang }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const isRTL = lang === "ar";

  const click = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (loading || disabled || !source || !source.trim()) return;
    setLoading(true);
    setError(false);
    try {
      const targetName = target === "ar" ? "Arabic" : "English";
      const r = await authFetch("/api/ai-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: 80,
          messages: [{
            role: "user",
            content: `Translate this ${domain} to ${targetName}. Respond with only the translation, no quotes, no extra text.${glossaryClause(target)}\n\n${domain}: ${source.trim()}`
          }]
        })
      });
      const d = await r.json();
      const out = (d.content?.[0]?.text || "").trim().replace(/^["']|["']$/g, "");
      if (out) onResult?.(out);
      else setError(true);
    } catch {
      setError(true);
    }
    setLoading(false);
    if (error) setTimeout(() => setError(false), 2500);
  };

  const tooltip = isRTL
    ? (target === "ar" ? "ترجمة بالذكاء الاصطناعي" : "Translate with AI to English")
    : (target === "ar" ? "Translate to Arabic" : "Translate to English");

  return (
    <button
      type="button"
      onClick={click}
      disabled={loading || disabled || !source || !source.trim()}
      title={tooltip}
      aria-label={tooltip}
      style={{
        height: 28, padding: "0 8px",
        borderRadius: 6, border: "1px solid var(--gold-bd, var(--g200))",
        background: error ? "var(--red-bg, #fee)" : "var(--gold-bg, #fff8e1)",
        color: error ? "var(--red, #c00)" : "var(--gold, #b8870c)",
        cursor: loading || disabled || !source?.trim() ? "default" : "pointer",
        fontSize: 11, fontWeight: 600,
        opacity: !source?.trim() ? 0.45 : 1,
        display: "inline-flex", alignItems: "center", gap: 4,
        flexShrink: 0,
        ...style,
      }}
    >
      {loading ? "…" : error ? "✗" : (target === "ar" ? "AR ⚡" : "EN ⚡")}
    </button>
  );
}
